import _ from 'lodash';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  ICompleteMultipartUploadResponse,
  IS3UploadAdapter,
  useS3MultiUploader,
} from '../hooks';
import { useMultiFileUploadState } from '../state';
import { IFileState, MultiFileUpload } from './MultiFileUpload';

interface IPendingFiles {
  [fileName: string]: File;
}

const SUCCESS_DISPLAY_DURATION_MS = 2000;

export interface IS3MultiFileUploadProps {
  accept: string;
  adapter: IS3UploadAdapter;
  errorMessage?: string;
  /**
   * Array of initial files used to prepopulate the MultiFileUpload component.
   */
  initialFiles?: Array<{ file: File; fileId: number }>;
  isDisabled?: boolean;
  isInvalid?: boolean;
  label: string;
  maxFileSizeMb: number;
  onErrorStateChange?: (params: { hasErrors: boolean }) => void;
  /**
   * TODO (HCM): Revisit these props after tearing down old HCM. We want to
   * remove `fileId` as a dependency if we can.
   */
  onRemoveFile: (params: { fileId: number; file: File }) => void;
  onFilesSuccess?: (params: { fileId: number; file: File }[]) => void;
  /**
   * Callback for upload state changes.
   */
  onUploadStateChange?: (params: { isUploading: boolean }) => void;
}

export interface IS3MultiFileUploadRef {
  abortAllUploads: () => void;
}

export const S3MultiFileUpload = forwardRef<
  IS3MultiFileUploadRef,
  IS3MultiFileUploadProps
>(
  (
    {
      accept,
      adapter,
      errorMessage,
      initialFiles,
      isDisabled,
      isInvalid,
      label,
      maxFileSizeMb,
      onErrorStateChange,
      onFilesSuccess,
      onRemoveFile,
      onUploadStateChange,
    },
    ref
  ) => {
    const [pendingFiles, setPendingFiles] = useState<IPendingFiles>({});
    const [fileIdsByName, setFileIdsByName] = useState<Record<string, number>>(
      {}
    );

    const {
      fileStatesByName,
      updateFileState,
      removeFileState,
      mergeFileStates,
      setFileError,
    } = useMultiFileUploadState();

    const fileStatesByNameRef =
      useRef<Record<string, IFileState>>(fileStatesByName);

    useEffect(() => {
      fileStatesByNameRef.current = fileStatesByName;
    }, [fileStatesByName]);

    const { abortAllUploads, getUploadPercentages, isUploading, uploadFiles } =
      useS3MultiUploader({
        accept,
        adapter,
        onFileProgress: ({ file, uploadPercentage }) => {
          updateFileState(file.name, { uploadPercentage });
        },
        onFileSuccess: (result: ICompleteMultipartUploadResponse<unknown>) => {
          const fileName = result.file.name;
          // Check if the response contains a URL (e.g., from public file uploads)
          const responseUrl = (result.response as any)?.fileUrl;

          // Update file state with URL if available
          updateFileState(fileName, {
            status: 'success',
            uploadPercentage: 100,
            ...(responseUrl && { url: responseUrl }),
          });

          // Transition to idle after display duration
          setTimeout(() => {
            updateFileState(fileName, {
              status: 'idle',
              uploadPercentage: 100,
              ...(responseUrl && { url: responseUrl }),
            });
          }, SUCCESS_DISPLAY_DURATION_MS);

          setPendingFiles((currentPendingFiles) => {
            const file = currentPendingFiles[fileName];
            return file
              ? _.omit(currentPendingFiles, fileName)
              : currentPendingFiles;
          });

          setFileIdsByName((prev) => ({
            ...prev,
            [fileName]: result.fileId,
          }));
        },
        onFilesSuccess: (
          results: ICompleteMultipartUploadResponse<unknown>[]
        ) => {
          onFilesSuccess?.(
            _.filter(
              results,
              (res) => !!fileStatesByNameRef.current[res.file.name]
            )
          );
        },
        onFileError: ({ file, error }) => {
          setPendingFiles((prev) => _.omit(prev, file.name));
          setFileIdsByName((currentFileIds) => {
            const fileId = currentFileIds[file.name];
            if (fileId) {
              onRemoveFile({ fileId, file });
              return _.omit(currentFileIds, file.name);
            }
            return currentFileIds;
          });
          setFileError(file.name, error.message);
        },
      });

    // Expose abortAllUploads via ref
    useImperativeHandle(ref, () => ({ abortAllUploads }), [abortAllUploads]);

    useEffect(() => {
      if (onErrorStateChange) {
        const filesInErrorState = _.filter(
          fileStatesByName,
          (state) => state.status === 'error'
        );
        const hasErrors = !_.isEmpty(filesInErrorState);
        onErrorStateChange({ hasErrors });
      }
    }, [fileStatesByName]);

    // React to upload state changes
    useEffect(() => {
      onUploadStateChange?.({ isUploading });
    }, [isUploading, onUploadStateChange]);

    // Abort all uploads on unmount
    useEffect(() => {
      return () => {
        abortAllUploads();
      };
    }, []);

    const uploadPercentages = getUploadPercentages();

    // Handle upload start state timing
    useEffect(() => {
      const newStates: Record<string, IFileState> = {};

      for (const [fileName, uploadPercentage] of Object.entries(
        uploadPercentages
      )) {
        const state = fileStatesByName[fileName];
        if (!state) {
          continue;
        }

        // Start tracking upload time when uploadPercentage goes from 0 to >0
        if (state.uploadPercentage === 0 && uploadPercentage > 0) {
          newStates[fileName] = {
            ...state,
            status: 'loading',
            uploadPercentage,
            uploadStartTime: Date.now(),
          };
        } else if (state.uploadPercentage !== uploadPercentage) {
          newStates[fileName] = {
            ...state,
            uploadPercentage,
          };
        }
      }

      if (!_.isEmpty(newStates)) {
        mergeFileStates(newStates);
      }
    }, [uploadPercentages, fileStatesByName]);

    // Initialize pre-uploaded files
    useEffect(() => {
      if (!_.isEmpty(initialFiles)) {
        const initialFileStates: Record<string, IFileState> = {};
        const initialFileIdsByName: Record<string, number> = {};

        for (const { file, fileId } of initialFiles) {
          initialFileStates[file.name] = {
            file,
            status: 'idle',
            uploadPercentage: 100,
          };
          initialFileIdsByName[file.name] = fileId;
        }

        mergeFileStates(initialFileStates);
        setFileIdsByName(initialFileIdsByName);
      }
    }, []);

    const handleChangeFiles = async (changedFiles: File[]) => {
      const newFileStatesByName: Record<string, IFileState> = {};
      const filesWithoutErrors: File[] = [];

      for (const file of changedFiles) {
        if (fileStatesByName[file.name]) {
          continue;
        }

        if (maxFileSizeMb && file.size > maxFileSizeMb * 1024 * 1024) {
          const error = `File size must be less than ${maxFileSizeMb}MB`;
          newFileStatesByName[file.name] = {
            file,
            status: 'error',
            error,
            uploadPercentage: 0,
          };
        } else {
          newFileStatesByName[file.name] = {
            file,
            status: 'loading',
            uploadPercentage: 0,
          };
          filesWithoutErrors.push(file);
        }
      }

      mergeFileStates(newFileStatesByName);

      if (!_.isEmpty(filesWithoutErrors)) {
        const newPendingFiles: IPendingFiles = {};
        for (const file of filesWithoutErrors) {
          if (!pendingFiles[file.name]) {
            newPendingFiles[file.name] = file;
          }
        }
        setPendingFiles((prev) => ({ ...prev, ...newPendingFiles }));
        await uploadFiles(filesWithoutErrors);
      }
    };

    const handleRemoveFile = (file: File) => {
      removeFileState(file.name);
      setFileIdsByName((prev) => _.omit(prev, file.name));
      onRemoveFile({ fileId: fileIdsByName[file.name], file });
    };

    return (
      <MultiFileUpload
        accept={accept}
        errorMessage={errorMessage}
        fileStatesByName={fileStatesByName}
        handleRemoveFile={handleRemoveFile}
        isDisabled={isDisabled || isUploading}
        isInvalid={isInvalid}
        label={label}
        onChange={handleChangeFiles}
      />
    );
  }
);

S3MultiFileUpload.displayName = 'S3MultiFileUpload';
